/**
 * QMS Trend Summary Widget
 * Displays a compact summary of detected patterns and severity distribution
 */

import { AlertTriangle, TrendingUp, Activity, Calendar, Clock3 } from 'lucide-react';
import { format } from 'date-fns';
import { useQmsTrends } from '../../hooks/useQmsTrends';
import type { QmsPatternSeverity } from '../../types/qms.types';
import { getTrendFreshness } from './qmsTrendPresentation';

const SEVERITY_COLORS: Record<QmsPatternSeverity, string> = {
  critical: 'text-red-600 bg-red-50 border-red-200',
  high: 'text-orange-600 bg-orange-50 border-orange-200',
  medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  low: 'text-blue-600 bg-blue-50 border-blue-200',
};

const SEVERITY_LABELS: Record<QmsPatternSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

interface QmsTrendSummaryWidgetProps {
  now?: Date;
}

export function QmsTrendSummaryWidget({ now }: QmsTrendSummaryWidgetProps) {
  const { data: trends, isLoading, isError } = useQmsTrends();

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="h-5 w-5 text-gray-400 animate-pulse" />
          <h3 className="text-sm font-medium text-gray-900">Trend Analysis</h3>
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
        </div>
      </div>
    );
  }

  if (isError || !trends || !trends.has_data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-5 w-5 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900">Trend Analysis</h3>
        </div>
        <p className="text-sm text-gray-500">
          {trends?.message || 'No trend data available yet. Analysis runs daily at 06:00 UTC.'}
        </p>
      </div>
    );
  }

  const { summary, date, computed_at, total_tickets_analyzed } = trends;

  const totalPatterns = summary?.total_patterns || 0;
  const criticalCount = summary?.critical || 0;
  const highCount = summary?.high || 0;
  const freshness = getTrendFreshness(computed_at, now);

  const hasAlerts = criticalCount > 0 || highCount > 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-900">Trend Analysis</h3>
        </div>
        {hasAlerts && (
          <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100 border border-red-300 rounded-full">
            <AlertTriangle className="h-3 w-3 text-red-600" />
            <span className="text-xs font-medium text-red-700">
              {criticalCount + highCount} Alert{criticalCount + highCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="text-center p-2 bg-gray-50 rounded-md">
          <div className="text-2xl font-bold text-gray-900">{totalPatterns}</div>
          <div className="text-xs text-gray-600">Patterns Detected</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-md">
          <div className="text-2xl font-bold text-gray-900">{total_tickets_analyzed || 0}</div>
          <div className="text-xs text-gray-600">Tickets In Scope</div>
        </div>
      </div>

      {freshness.isStale && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <div className="flex items-start gap-2">
            <Clock3 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
            <div>
              <p className="text-xs font-semibold text-amber-900">Stale analysis</p>
              <p className="text-xs text-amber-800">{freshness.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Severity Breakdown */}
      {totalPatterns > 0 && summary && (
        <div className="space-y-1.5 mb-3">
          {(['critical', 'high', 'medium', 'low'] as QmsPatternSeverity[]).map((severity) => {
            const count = summary[severity] || 0;
            if (count === 0) return null;

            return (
              <div
                key={severity}
                className={`flex items-center justify-between px-2 py-1.5 rounded-md border ${SEVERITY_COLORS[severity]}`}
              >
                <span className="text-xs font-medium">{SEVERITY_LABELS[severity]}</span>
                <span className="text-xs font-bold">{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer - Last Update */}
      <div className="flex items-center gap-1 text-xs text-gray-500 pt-2 border-t border-gray-100">
        <Calendar className="h-3 w-3" />
        <span>
          {date ? format(new Date(date), 'MMM d, yyyy') : 'No analysis yet'}
          {computed_at && (
            <span className="ml-1">
              at {format(new Date(computed_at), 'HH:mm')}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
